import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const EMOTION_COLORS = {
  '理解できた': '#22c55e',
  '難しかった': '#ef4444',
  '面白かった': '#3b82f6',
  'もっと学びたい': '#8b5cf6',
  '達成感がある': '#f59e0b',
};

export default function ReviewAnalysisTab({ schoolType }) {
  const grades = schoolType === 'junior' ? [1,2,3] : [1,2,3,4,5,6];
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubjectName, setSelectedSubjectName] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [mode, setMode] = useState('class'); // 'class' or 'compare'
  const [analysisData, setAnalysisData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiComment, setAiComment] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const fetchSubjects = useCallback(async () => {
    try {
      const r = await api.get('/subjects');
      setSubjects(r.data);
    } catch(e) {}
  }, []);

  const fetchClasses = useCallback(async () => {
    try {
      const r = await api.get('/classes');
      setClasses(r.data);
    } catch(e) {}
  }, []);

  useEffect(() => { fetchSubjects(); fetchClasses(); }, [fetchSubjects, fetchClasses]);

  const subjectNames = [...new Set(
    subjects.filter(s => !selectedGrade || String(s.grade) === selectedGrade)
      .map(s => s.subject_name)
  )];

  const units = subjects.filter(s =>
    (!selectedGrade || String(s.grade) === selectedGrade) &&
    (!selectedSubjectName || s.subject_name === selectedSubjectName)
  );

  const filteredClasses = classes.filter(c =>
    !selectedGrade || String(c.grade) === selectedGrade
  );

  const handleAnalyze = async () => {
    if (!selectedUnitId) { alert('単元を選択してください'); return; }
    setLoading(true);
    setAnalysisData(null);
    setCompareData(null);
    setAiComment('');
    try {
      if (mode === 'class') {
        const params = new URLSearchParams({ subject_id: selectedUnitId });
        if (selectedClassId) params.append('class_id', selectedClassId);
        const r = await api.get('/analysis/class?' + params.toString());
        setAnalysisData(r.data);
      } else {
        const r = await api.get('/analysis/compare?subject_id=' + selectedUnitId);
        setCompareData(r.data);
      }
    } catch(e) {
      alert('分析に失敗しました: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  const handleAiComment = async () => {
    if (!analysisData) return;
    setAiLoading(true);
    try {
      const r = await api.post('/analysis/ai-comment', {
        subject_id: selectedUnitId,
        class_id: selectedClassId || null,
        summary: analysisData.summary
      });
      setAiComment(r.data.comment);
    } catch(e) {
      setAiComment('AI分析の生成に失敗しました');
    } finally { setAiLoading(false); }
  };

  const handleDownloadCsv = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ subject_id: selectedUnitId });
      if (selectedClassId) params.append('class_id', selectedClassId);
      const r = await fetch('/api/analysis/csv?' + params.toString(), {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'analysis.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('CSV出力に失敗しました'); }
  };

  const S = {
    wrap: { padding: 20 },
    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 },
    label: { fontSize: 12, color: '#6b7280', marginBottom: 4, display: 'block' },
    select: { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 },
    btn: { padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#fff' },
    btnPrimary: { padding: '8px 20px', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer', background: '#2d4a8a', color: '#fff', fontWeight: 600 },
    btnSuccess: { padding: '6px 14px', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#059669', color: '#fff' },
    modeBtn: (active) => ({ padding: '8px 20px', border: active ? 'none' : '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: active ? '#2d4a8a' : '#fff', color: active ? '#fff' : '#374151', fontWeight: active ? 600 : 400 }),
    statCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', textAlign: 'center', minWidth: 80 },
    statNum: { fontSize: 24, fontWeight: 700, color: '#1e40af' },
    statLabel: { fontSize: 11, color: '#6b7280', marginTop: 2 },
    badge: (color) => ({ display: 'inline-block', background: color + '20', color: color, padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, margin: 2 }),
    tableRow: (warn) => ({ borderBottom: '1px solid #f1f5f9', background: warn ? '#fff7ed' : 'transparent' }),
  };

  return (
    <div style={S.wrap}>
      {/* 絞り込みパネル */}
      <div style={S.card}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button style={S.modeBtn(mode === 'class')} onClick={() => setMode('class')}>クラス詳細分析</button>
          <button style={S.modeBtn(mode === 'compare')} onClick={() => setMode('compare')}>クラス横断比較</button>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={S.label}>学年</label>
            <select style={S.select} value={selectedGrade} onChange={e => { setSelectedGrade(e.target.value); setSelectedSubjectName(''); setSelectedUnitId(''); }}>
              <option value="">全学年</option>
              {grades.map(g => <option key={g} value={g}>{g}年</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>教科</label>
            <select style={S.select} value={selectedSubjectName} onChange={e => { setSelectedSubjectName(e.target.value); setSelectedUnitId(''); }}>
              <option value="">選択</option>
              {subjectNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>単元</label>
            <select style={S.select} value={selectedUnitId} onChange={e => setSelectedUnitId(e.target.value)}>
              <option value="">選択</option>
              {units.map(s => <option key={s.id} value={s.id}>{s.unit_name || s.subject_name}</option>)}
            </select>
          </div>
          {mode === 'class' && (
            <div>
              <label style={S.label}>クラス</label>
              <select style={S.select} value={selectedClassId} onChange={e => setSelectedClassId(e.target.value)}>
                <option value="">全クラス</option>
                {filteredClasses.map(c => <option key={c.id} value={c.id}>{c.grade}年{c.class_name}組</option>)}
              </select>
            </div>
          )}
          <button style={S.btnPrimary} onClick={handleAnalyze} disabled={loading}>
            {loading ? '分析中...' : '🔍 分析する'}
          </button>
        </div>
      </div>

      {/* クラス詳細分析結果 */}
      {mode === 'class' && analysisData && (
        <>
          <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e40af' }}>
                {analysisData.subject_name}「{analysisData.unit_name}」
                {analysisData.class_name ? ` ${analysisData.class_name}` : ' 全クラス'}
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.btnSuccess} onClick={handleDownloadCsv}>📥 CSV出力</button>
              </div>
            </div>

            {/* サマリー */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              <div style={S.statCard}>
                <div style={S.statNum}>{analysisData.summary.total_students}</div>
                <div style={S.statLabel}>対象児童数</div>
              </div>
              <div style={S.statCard}>
                <div style={{ ...S.statNum, color: '#059669' }}>{analysisData.summary.submitted}</div>
                <div style={S.statLabel}>提出済み</div>
              </div>
              <div style={S.statCard}>
                <div style={{ ...S.statNum, color: '#ef4444' }}>{analysisData.summary.not_submitted}</div>
                <div style={S.statLabel}>未提出</div>
              </div>
              <div style={S.statCard}>
                <div style={{ ...S.statNum, color: '#6b7280' }}>{analysisData.summary.submission_rate}%</div>
                <div style={S.statLabel}>提出率</div>
              </div>
              <div style={S.statCard}>
                <div style={{ ...S.statNum, color: '#8b5cf6' }}>{analysisData.summary.avg_length}文字</div>
                <div style={S.statLabel}>平均記述量</div>
              </div>
              <div style={S.statCard}>
                <div style={{ ...S.statNum, color: '#f59e0b' }}>{analysisData.summary.avg_count}回</div>
                <div style={S.statLabel}>平均提出回数</div>
              </div>
            </div>

            {/* 感情タグ集計 */}
            {analysisData.summary.emotion_totals && Object.keys(analysisData.summary.emotion_totals).length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>感情タグ集計</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {Object.entries(analysisData.summary.emotion_totals).sort((a,b) => b[1]-a[1]).map(([tag, count]) => (
                    <span key={tag} style={S.badge(EMOTION_COLORS[tag] || '#6b7280')}>
                      {tag} {count}件
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* AI分析コメント */}
            <div style={{ background: '#f0f4ff', borderRadius: 8, padding: 14, marginBottom: 16 }}>
              {aiComment ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af', marginBottom: 6 }}>🤖 AI分析コメント</div>
                  <div style={{ fontSize: 13, color: '#1e3a8a', lineHeight: 1.7 }}>{aiComment}</div>
                  <button style={{ ...S.btn, marginTop: 8, fontSize: 11 }} onClick={handleAiComment} disabled={aiLoading}>
                    {aiLoading ? '生成中...' : '再生成'}
                  </button>
                </>
              ) : (
                <button style={S.btnPrimary} onClick={handleAiComment} disabled={aiLoading}>
                  {aiLoading ? '🤖 AI分析生成中...' : '🤖 AIでクラス全体を分析する'}
                </button>
              )}
            </div>
          </div>

          {/* 児童別一覧 */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#374151' }}>児童別ふりかえり状況</h3>
            {Object.entries(
              analysisData.students.reduce((acc, s) => {
                const key = `${s.grade}年${s.class_name}組`;
                if (!acc[key]) acc[key] = [];
                acc[key].push(s);
                return acc;
              }, {})
            ).map(([cls, students]) => (
              <div key={cls} style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, color: '#1e40af', fontSize: 13, marginBottom: 8 }}>{cls}</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', width: 60 }}>番号</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', width: 60 }}>提出回数</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', width: 70 }}>平均文字数</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>感情タグ</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>最新ふりかえり</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(s => (
                      <tr key={s.number} style={S.tableRow(s.review_count === 0 || s.review_count < 2)}>
                        <td style={{ padding: '8px 10px' }}>
                          {s.number}番
                          {s.review_count === 0 && <span style={{ color: '#ef4444', fontSize: 11, marginLeft: 4 }}>未提出</span>}
                          {s.review_count > 0 && s.review_count < 2 && <span style={{ color: '#f97316', fontSize: 11, marginLeft: 4 }}>少ない</span>}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          {s.review_count > 0
                            ? <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 99, fontSize: 12 }}>{s.review_count}回</span>
                            : <span style={{ color: '#9ca3af' }}>-</span>}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'center', color: s.avg_length < 20 ? '#ef4444' : '#374151' }}>
                          {s.avg_length > 0 ? s.avg_length + '文字' : '-'}
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          {s.emotion_tags?.map(t => (
                            <span key={t} style={S.badge(EMOTION_COLORS[t] || '#6b7280')}>{t}</span>
                          ))}
                        </td>
                        <td style={{ padding: '8px 10px', fontSize: 12, color: '#6b7280', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.latest_content || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </>
      )}

      {/* クラス横断比較結果 */}
      {mode === 'compare' && compareData && (
        <div style={S.card}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e40af', marginBottom: 16 }}>
            {compareData.subject_name}「{compareData.unit_name}」クラス比較
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>クラス</th>
                <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>提出率</th>
                <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>平均回数</th>
                <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>平均文字数</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>多い感情タグ</th>
              </tr>
            </thead>
            <tbody>
              {compareData.classes.map(c => (
                <tr key={c.class_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{c.grade}年{c.class_name}組</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <div style={{ background: '#e2e8f0', borderRadius: 99, width: 80, height: 8, overflow: 'hidden' }}>
                        <div style={{ background: c.submission_rate >= 80 ? '#22c55e' : c.submission_rate >= 50 ? '#f59e0b' : '#ef4444', width: c.submission_rate + '%', height: '100%' }} />
                      </div>
                      <span style={{ color: c.submission_rate >= 80 ? '#059669' : c.submission_rate >= 50 ? '#d97706' : '#dc2626', fontWeight: 600 }}>{c.submission_rate}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>{c.avg_count}回</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>{c.avg_length}文字</td>
                  <td style={{ padding: '10px 12px' }}>
                    {c.top_emotions?.map(t => (
                      <span key={t} style={S.badge(EMOTION_COLORS[t] || '#6b7280')}>{t}</span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!analysisData && !compareData && !loading && (
        <div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: 40 }}>
          学年・教科・単元を選んで「分析する」ボタンを押してください
        </div>
      )}
    </div>
  );
}
